<template>
  <div
    ref="root"
    class="round-slider"
    :style="{ width: size + 'px', height: size + 'px' }"
  >
    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
      <defs>
        <linearGradient id="arcGradient" gradientUnits="userSpaceOnUse" x1="-100" y1="0" x2="100" y2="0">
          <stop offset="0%" stop-color="#822312" />
          <stop offset="20%" stop-color="#CD5500" />
          <stop offset="40%" stop-color="#EDAE39" />
          <stop offset="60%" stop-color="#92C93A" />
          <stop offset="80%" stop-color="#00AF00" />
        </linearGradient>
      </defs>
      <g :transform="`translate(${size/2}, ${size/2})`">
        <!-- Background arc (inactive) -->
        <path
          :d="arcPath"
          class="arc-bg"
          :stroke-width="strokeWidth"
          stroke-linecap="round"
          fill="none"
        />

        <!-- Active range between handles -->
        <path
          v-if="activePath"
          :d="activePath"
          class="arc-active"
          :stroke-width="strokeWidth"
          stroke-linecap="round"
          fill="none"
          stroke="url(#arcGradient)"
        />

        <!-- First handle -->
        <g
          :key="0"
          class="handle-group"
          :transform="`translate(${handles[0].x}, ${handles[0].y})`"
        >
          <!-- visible little knob -->
          <circle
            @pointerdown.prevent="startDrag(0, $event)"
            @keydown.prevent="onHandleKey(0, $event)"
            class="handle"
            :r="strokeWidth * 0.9"
          />
          <text 
            text-anchor="middle"
            alignment-baseline="middle" 
            class="handle-value"
          >
            {{ Math.round(values[0]) }}
          </text>
        </g>

        <!-- Second handle -->
        <g
          :key="1"
          class="handle-group"
          :transform="`translate(${handles[1].x}, ${handles[1].y})`"
        >
          <!-- visible little knob -->
          <circle
            @pointerdown.prevent="startDrag(1, $event)"
            @keydown.prevent="onHandleKey(1, $event)"
            class="handle"
            :r="strokeWidth * 0.9"
          />
          <text 
            text-anchor="middle"
            alignment-baseline="middle" 
            class="handle-value"
          >
            {{ Math.round(values[1]) }}
          </text>
        </g>
      </g>
    </svg>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

// Props
const props = defineProps({
  modelValue: { type: Array, default: () => [20, 80] }, // two values
  min: { type: Number, default: 0 },
  max: { type: Number, default: 99 },
  size: { type: Number, default: 75 },
  strokeWidth: { type: Number, default: 10 },
  gap: { type: Number, default: 90 }, // degrees of opening (gap) centered at bottom
})

const emit = defineEmits(['update:modelValue'])

// keep internal mutable copy
const values = ref([...props.modelValue])
watch(() => props.modelValue, v => { values.value = [...v] })

// refs
const root = ref(null) // used to compute center rect and capture

// Geometry helpers
const radius = computed(() => (props.size - props.strokeWidth) / 2)

// Gap is centered at bottom (270deg). We'll make the arc run from startAngle to endAngle in degrees.
const gapCenter = 180
const halfGap = computed(() => Math.max(0, Math.min(359, props.gap)) / 2)
const startAngle = computed(() => gapCenter + halfGap.value) // degrees
const endAngle = computed(() => gapCenter - halfGap.value + 360) // degrees (end > start)
const sweepAngle = computed(() => endAngle.value - startAngle.value) // degrees - how much arc exists

// Convert value -> angle along the arc (degrees)
function valueToAngle(v) {
  const t = (v - props.min) / (props.max - props.min)
  return startAngle.value + t * sweepAngle.value
}

// Convert polar to cartesian
function polarToCartesian(angleDeg, r = radius.value) {
  const a = (angleDeg - 90) * (Math.PI / 180) // svg coord: 0deg at 12 o'clock
  return {
    x: Math.cos(a) * r,
    y: Math.sin(a) * r,
  }
}

// Build the background arc path (single arc representing the full available range)
const arcPath = computed(() => {
  const s = polarToCartesian(startAngle.value)
  const e = polarToCartesian(endAngle.value)
  const largeArc = sweepAngle.value > 180 ? 1 : 0
  return `M ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${radius.value} ${radius.value} 0 ${largeArc} 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`
})

// Handles positions
const handles = computed(() => {
  return values.value.map(v => {
    const a = valueToAngle(v)
    const p = polarToCartesian(a)
    return { angle: a, x: p.x, y: p.y, value: v }
  })
})

// Active path (arc between handle0 and handle1 along the arc direction we use for values)
const activePath = computed(() => {
  // order handles by their numeric value
  const sorted = [...values.value].sort((a, b) => a - b)
  const a1 = valueToAngle(sorted[0])
  const a2 = valueToAngle(sorted[1])
  const p1 = polarToCartesian(a1)
  const p2 = polarToCartesian(a2)
  const largeArc = (a2 - a1) > 180 ? 1 : 0
  return `M ${p1.x.toFixed(3)} ${p1.y.toFixed(3)} A ${radius.value} ${radius.value} 0 ${largeArc} 1 ${p2.x.toFixed(3)} ${p2.y.toFixed(3)}`
})

// Helpers to convert angle -> value while clamping to allowed arc
function angleToValue(angle) {
  // normalize input angle to [0,360)
  let a = angle % 360
  if (a < 0) a += 360
  // bring into the [startAngle, endAngle] range by adding 360 if needed
  if (a < startAngle.value) a += 360
  if (a > endAngle.value) a = endAngle.value
  const t = (a - startAngle.value) / sweepAngle.value
  return props.min + t * (props.max - props.min)
}

// Pointer drag handling (robust)
let dragging = null // index of handle being dragged
let activePointerElement = null
let activePointerId = null

function startDrag(index, ev) {
  dragging = index
  activePointerElement = ev.target
  activePointerId = ev.pointerId
  try { ev.target.setPointerCapture(ev.pointerId) } catch (e) { /* some browsers */ }
}

function movePointer(ev) {
  if (dragging === null) return
  if (!root.value) return

  const rect = root.value.getBoundingClientRect()
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const dx = ev.clientX - cx
  const dy = ev.clientY - cy
  const rawAngle = Math.atan2(dy, dx) * 180 / Math.PI + 90 // 0 at top
  let angle = rawAngle
  if (angle < 0) angle += 360

  // clamp angle to allowed arc (angleToValue will also clamp)
  const newVal = Math.max(props.min, Math.min(props.max, angleToValue(angle)))

  if (dragging === 1 && newVal - 2 <= values.value[0]) return;
  else if (dragging === 0 && newVal + 2 >= values.value[1]) return;

  // write value
  values.value[dragging] = Number(Math.floor(newVal))
  emit('update:modelValue', [...values.value])
}

function endPointer(ev) {
  if (dragging === null) return
  // release capture on the element that captured it
  try {
    if (activePointerElement && typeof activePointerElement.releasePointerCapture === 'function') {
      activePointerElement.releasePointerCapture(activePointerId)
    }
  } catch (e) {}
  dragging = null
  activePointerElement = null
  activePointerId = null
}

onMounted(() => {
  document.addEventListener('pointermove', movePointer)
  document.addEventListener('pointerup', endPointer)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointermove', movePointer)
  document.removeEventListener('pointerup', endPointer)
})

// Keyboard control for accessibility
function onHandleKey(index, e) {
  const range = props.max - props.min
  const step = range / 100
  if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
    values.value[index] = Math.max(props.min, values.value[index] - step)
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
    values.value[index] = Math.min(props.max, values.value[index] + step)
  } else if (e.key === 'Home') {
    values.value[index] = props.min
  } else if (e.key === 'End') {
    values.value[index] = props.max
  }
  emit('update:modelValue', [...values.value])
}
</script>

<style scoped>
.round-slider {
  display: inline-block;
  position: relative;
  touch-action: none; /* important for pointer events */
}
svg {
  display: block;
  user-select: none;
  overflow: visible;
}
.arc-bg {
  stroke: #eee;
}
.arc-active {
}
.handle-group { }

.handle {
  fill: white;
  stroke: #111827;
  stroke-width: 2px;
}
.handle:hover {
  cursor: grab;
}
.handle:active {
  cursor: grabbing;
}
.handle:focus { outline: none; }

.handle-value {
  pointer-events: none;
  font-size: 10px;
}
</style>
